import {type CSSProperties, Suspense, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import clsx from 'clsx';
import {diApiEu} from '@di-shared/api/di';
import {getYYYYMMddFormat} from '@di-shared/api/di/utils';
import {ERROR, UNIT} from '@di-shared/const';
import {ExpectedError} from '@di-shared/features/common/CustomError';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import EnergyPowerRecentGraph from '@di-shared/features/history/energy-usage/EnergyPowerRecentGraph';
import {EnergyUsageRecentProps} from '@di-shared/features/history/energy-usage/EnergyUsageRecent/index';
import {roundToFirstDecimal} from '@di-shared/features/history/energy-usage/utils';
import {formatNumberWithCommas} from '@di-shared/utils';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import useUnitConverter from '@di-shared/utils/useUnitConverter';
import {parseDateWithFormat, subDate} from '@shared/dateUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import {useSuspenseQuery} from '@tanstack/react-query';

export default function ({vin}: EnergyUsageRecentProps) {
  return (
    <ErrorBoundary fallback={<FallbackError />}>
      <Suspense fallback={<FallbackLoading />}>
        <Inner vin={vin} />
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner({vin}: EnergyUsageRecentProps) {
  const {data: evRecentData} = useSuspenseQuery({
    queryKey: [getQueryKey(diApiEu.drivingHistory.retrieveEvStatsOfLastDrive), vin],
    queryFn: () => diApiEu.drivingHistory.retrieveEvStatsOfLastDrive({vin}),
    select: ({data}) => data,
  });

  const isRecentEnergyUsageNone =
    evRecentData.latestDriveDate === '' &&
    evRecentData.powerConsumption === 0 &&
    evRecentData.motorConsumptionRatio === 0 &&
    evRecentData.climateConsumptionRatio === 0 &&
    evRecentData.electricDeviceConsumptionRatio === 0 &&
    evRecentData.batteryManageConsumptionRatio === 0 &&
    evRecentData.avrMileage === 0 &&
    evRecentData.regenPower === 0;

  // No data 화면
  if (isRecentEnergyUsageNone) {
    return <None />;
  }

  // Default 화면
  return <Default vin={vin} />;
}

function None() {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {defaultEnergyEfficiencySymbol, defaultEnergyEfficiencySecondarySymbol} = useUnitConverter();
  const today = new Date();

  return (
    <section className="section">
      <div className="section_heading type_main">
        <h3
          className="section_heading_title"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('msg_mytrips_ev_title_02')}
        </h3>
        <span
          className="section_heading_caption"
          role="text"
          aria-label={`${t('ctn_mytrips_ev_title_update_01')} ${CldrDatetimeFormatter.format(subDate(today, {days: 1}), 'yMMMd')}`}
        >
          {t('ctn_mytrips_ev_title_update_01')} {CldrDatetimeFormatter.format(subDate(today, {days: 1}), 'yMMMd')}
        </span>
      </div>
      <div className="section_container">
        <div className="section_container_header type_border">
          <div
            className="device_info_item"
            role="text"
            aria-label={`${t('ctn_mytrips_ev_energyuse')} ${0}${UNIT.KWH}`}
          >
            <h4
              className="device_info_key body_medium_b"
              aria-hidden={true}
            >
              {t('ctn_mytrips_ev_energyuse')}
            </h4>
            <span className="device_info_val body_xlarge_b">
              <span className="device_info_amount body_large_b">
                {0}
                <span className="body_small_r">{UNIT.KWH}</span>
              </span>
            </span>
          </div>
        </div>
        <div className="electricity_graph_area">
          <EnergyPowerRecentGraph
            motorRatio={0}
            electricDeviceRatio={0}
            climateRatio={0}
            batteryManageRatio={0}
          />
          <div className="electricty_graph_info">
            <ul className="graph_legend_list">
              <li
                className="graph_legend_item"
                role="text"
              >
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--graph-gauge-active)'}}
                />
                <span className="legend_name">{t('ctn_mytrips_ev_energyuse_driving')}</span>
                <span className="legend_value">{0}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
              <li
                className="graph_legend_item"
                role="text"
              >
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--blue-custom)'}}
                />
                <span className="legend_name">{t('ctn_mytrips_ev_energyuse_electronics')}</span>
                <span className="legend_value">{0}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
              <li
                className="graph_legend_item"
                role="text"
              >
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--di-mint)'}}
                />
                <span className="legend_name">{t('ctn_mytrips_ev_energyuse_climate')}</span>
                <span className="legend_value">{0}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
              <li
                className="graph_legend_item"
                role="text"
              >
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--di-purple)'}}
                />
                <span className="legend_name">{t('ctn_mytrips_ev_energyuse_battery')}</span>
                <span className="legend_value">{0}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <BarChartNone />
      <div
        className="section_container section_container_small"
        role="text"
        aria-label={`${t('ctn_mytrips_ev_average_energyuse')} ${0}${defaultEnergyEfficiencySymbol}${defaultEnergyEfficiencySecondarySymbol !== null ? ` = 0${defaultEnergyEfficiencySecondarySymbol}` : ''}`}
      >
        <div
          className="device_info_item"
          aria-label={`${t('ctn_mytrips_ev_average_energyuse')} ${0}${defaultEnergyEfficiencySymbol}${defaultEnergyEfficiencySecondarySymbol !== null ? ` = 0${defaultEnergyEfficiencySecondarySymbol}` : ''}`}
        >
          <span className="device_info_icon">
            <span className="ic ic_battery_24px" />
          </span>
          <div className="device_info_text">
            <h4
              className="device_info_key"
              aria-hidden={true}
            >
              {t('ctn_mytrips_ev_average_energyuse')}
            </h4>
            <span className="device_info_val">
              <span className="device_info_amount">
                <span className="device_info_number">{0}</span>
                <span className="device_info_unit">{defaultEnergyEfficiencySymbol}</span>
                {defaultEnergyEfficiencySecondarySymbol !== null && (
                  <span className="device_info_convert">
                    =<span className="device_info_number">{0}</span>
                    <span className="device_info_unit">{defaultEnergyEfficiencySecondarySymbol}</span>
                  </span>
                )}
              </span>
            </span>
          </div>
        </div>
      </div>
      <div
        className="section_container section_container_small"
        role="text"
        aria-label={`${t('ctn_mytrips_ev_regenerative_01')} ${0}${UNIT.KWH}`}
      >
        <div
          className="device_info_item"
          aria-label={`${t('ctn_mytrips_ev_regenerative_01')} ${0}${UNIT.KWH}`}
        >
          <span className="device_info_icon">
            <span className="ic ic_reservation_charge_24px" />
          </span>
          <div className="device_info_text">
            <h4
              className="device_info_key"
              aria-hidden={true}
            >
              {t('ctn_mytrips_ev_regenerative_01')}
            </h4>
            <span className="device_info_val">
              <span className="device_info_amount">
                <span className="device_info_number">{0}</span>
                <span className="device_info_unit">{UNIT.KWH}</span>
              </span>
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

function Default({vin}: EnergyUsageRecentProps) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {
    convertEnergyEfficiencyValue,
    convertEnergyEfficiencySymbol,
    convertEnergyEfficiencySecondaryValue,
    defaultEnergyEfficiencySecondarySymbol,
  } = useUnitConverter();
  const {data: evRecentData} = useSuspenseQuery({
    queryKey: [getQueryKey(diApiEu.drivingHistory.retrieveEvStatsOfLastDrive), vin],
    queryFn: () => diApiEu.drivingHistory.retrieveEvStatsOfLastDrive({vin}),
    select: ({data}) => data,
  });

  const verifiedRecentDrivingDate = parseDateWithFormat(evRecentData.latestDriveDate);
  if (!verifiedRecentDrivingDate) {
    throw new ExpectedError('latestDriveDate error');
  }

  const averageEnergyEfficiencyValue = convertEnergyEfficiencyValue(evRecentData.avrMileage);
  const roundedAverageEnergyEfficiencyValue =
    typeof averageEnergyEfficiencyValue === 'number' ? roundToFirstDecimal(averageEnergyEfficiencyValue) : averageEnergyEfficiencyValue;
  const averageEnergyEfficiencySecondaryValue = convertEnergyEfficiencySecondaryValue(evRecentData.avrMileage);
  const roundedAverageEnergyEfficiencySecondaryValue =
    typeof averageEnergyEfficiencySecondaryValue === 'number'
      ? roundToFirstDecimal(averageEnergyEfficiencySecondaryValue)
      : averageEnergyEfficiencySecondaryValue;
  const roundedPowerConsumption = roundToFirstDecimal(evRecentData.powerConsumption);
  const roundedRegenPower = roundToFirstDecimal(evRecentData.regenPower);

  return (
    <section className="section">
      <div className="section_heading type_main">
        <h3
          className="section_heading_title"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('msg_mytrips_ev_title_01')}
        </h3>
        <span
          className="section_heading_caption"
          role="text"
          aria-label={`${t('ctn_mytrips_ev_title_update_01')} ${CldrDatetimeFormatter.format(verifiedRecentDrivingDate, 'yMMMd')}`}
        >
          {t('ctn_mytrips_ev_title_update_01')} {CldrDatetimeFormatter.format(verifiedRecentDrivingDate, 'yMMMd')}
        </span>
      </div>
      <div className="section_container">
        <div className="section_container_header type_border">
          <div
            className="device_info_item"
            role="text"
            aria-label={`${t('ctn_mytrips_ev_energyuse')} ${formatNumberWithCommas(roundedPowerConsumption)}${UNIT.KWH}`}
          >
            <h4
              className="device_info_key body_medium_b"
              aria-hidden={true}
            >
              {t('ctn_mytrips_ev_energyuse')}
            </h4>
            <span
              className="device_info_val body_xlarge_b"
              aria-hidden={true}
            >
              <span className="device_info_amount body_large_b">
                {formatNumberWithCommas(roundedPowerConsumption)}
                <span className="body_small_r">{UNIT.KWH}</span>
              </span>
            </span>
          </div>
        </div>
        <div className="electricity_graph_area">
          <EnergyPowerRecentGraph
            motorRatio={evRecentData.motorConsumptionRatio}
            electricDeviceRatio={evRecentData.electricDeviceConsumptionRatio}
            climateRatio={evRecentData.climateConsumptionRatio}
            batteryManageRatio={evRecentData.batteryManageConsumptionRatio}
          />
          <div className="electricty_graph_info">
            <ul className="graph_legend_list">
              <li
                className="graph_legend_item"
                role="text"
                aria-label={`${t('ctn_mytrips_ev_energyuse_driving')} ${evRecentData.motorConsumptionRatio}${UNIT.PERCENT}`}
              >
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--graph-gauge-active)'}}
                />
                <span className="legend_name">{t('ctn_mytrips_ev_energyuse_driving')}</span>
                <span className="legend_value">{evRecentData.motorConsumptionRatio}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
              <li
                className="graph_legend_item"
                role="text"
                aria-label={`${t('ctn_mytrips_ev_energyuse_electronics')} ${evRecentData.electricDeviceConsumptionRatio}${UNIT.PERCENT}`}
              >
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--blue-custom)'}}
                />
                <span className="legend_name">{t('ctn_mytrips_ev_energyuse_electronics')}</span>
                <span className="legend_value">{evRecentData.electricDeviceConsumptionRatio}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
              <li
                className="graph_legend_item"
                role="text"
                aria-label={`${t('ctn_mytrips_ev_energyuse_climate')} ${evRecentData.climateConsumptionRatio}${UNIT.PERCENT}`}
              >
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--di-mint)'}}
                />
                <span className="legend_name">{t('ctn_mytrips_ev_energyuse_climate')}</span>
                <span className="legend_value">{evRecentData.climateConsumptionRatio}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
              <li
                className="graph_legend_item"
                role="text"
                aria-label={`${t('ctn_mytrips_ev_energyuse_battery')} ${evRecentData.batteryManageConsumptionRatio}${UNIT.PERCENT}`}
              >
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--di-purple)'}}
                />
                <span className="legend_name">{t('ctn_mytrips_ev_energyuse_battery')}</span>
                <span className="legend_value">{evRecentData.batteryManageConsumptionRatio}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <ErrorBoundary fallback={<BarChartError />}>
        <Suspense fallback={<BarChartLoading />}>
          <BarChart vin={vin} />
        </Suspense>
      </ErrorBoundary>
      <div
        className="section_container section_container_small"
        role="text"
        aria-label={`${t('ctn_mytrips_ev_average_energyuse')} ${formatNumberWithCommas(roundedAverageEnergyEfficiencyValue)}${convertEnergyEfficiencySymbol(evRecentData.avrMileage)}${defaultEnergyEfficiencySecondarySymbol !== null ? ` = ${formatNumberWithCommas(roundedAverageEnergyEfficiencySecondaryValue)}${defaultEnergyEfficiencySecondarySymbol}` : ''}`}
      >
        <div
          className="device_info_item"
          aria-label={`${t('ctn_mytrips_ev_average_energyuse')} ${formatNumberWithCommas(roundedAverageEnergyEfficiencyValue)}${convertEnergyEfficiencySymbol(evRecentData.avrMileage)}${defaultEnergyEfficiencySecondarySymbol !== null ? ` = ${formatNumberWithCommas(roundedAverageEnergyEfficiencySecondaryValue)}${defaultEnergyEfficiencySecondarySymbol}` : ''}`}
        >
          <span className="device_info_icon">
            <span className="ic ic_battery_24px" />
          </span>
          <div className="device_info_text">
            <h4
              className="device_info_key"
              aria-hidden={true}
            >
              {t('ctn_mytrips_ev_average_energyuse')}
            </h4>
            <span className="device_info_val">
              <span className="device_info_amount">
                <span className="device_info_number">{formatNumberWithCommas(roundedAverageEnergyEfficiencyValue)}</span>
                <span className="device_info_unit">{convertEnergyEfficiencySymbol(evRecentData.avrMileage)}</span>
                {defaultEnergyEfficiencySecondarySymbol !== null && (
                  <span className="device_info_convert">
                    =<span className="device_info_number">{formatNumberWithCommas(roundedAverageEnergyEfficiencySecondaryValue)}</span>
                    <span className="device_info_unit">{defaultEnergyEfficiencySecondarySymbol}</span>
                  </span>
                )}
              </span>
            </span>
          </div>
        </div>
      </div>
      <div
        className="section_container section_container_small"
        role="text"
        aria-label={`${t('ctn_mytrips_ev_regenerative_01')} ${formatNumberWithCommas(roundedRegenPower)}${UNIT.KWH}`}
      >
        <div
          className="device_info_item"
          aria-label={`${t('ctn_mytrips_ev_regenerative_01')} ${formatNumberWithCommas(roundedRegenPower)}${UNIT.KWH}`}
        >
          <span className="device_info_icon">
            <span className="ic ic_reservation_charge_24px" />
          </span>
          <div className="device_info_text">
            <h4
              className="device_info_key"
              aria-hidden={true}
            >
              {t('ctn_mytrips_ev_regenerative_01')}
            </h4>
            <span className="device_info_val">
              <span className="device_info_amount">
                <span className="device_info_number">{formatNumberWithCommas(roundedRegenPower)}</span>
                <span className="device_info_unit">{UNIT.KWH}</span>
              </span>
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

function FallbackLoading() {
  const {t} = useInferableTranslation();
  const {defaultEnergyEfficiencySymbol, defaultEnergyEfficiencySecondarySymbol} = useUnitConverter();

  return (
    <section
      className="section"
      aria-hidden="true"
    >
      <div className="section_heading type_main">
        <button
          type="button"
          className="btn_monthly body_medium_b skeleton"
          style={{width: '160px', height: '20px'}}
        />
        <h3
          className="section_heading_title skeleton"
          style={{width: '200px', height: '28px'}}
        />
        <span
          className="section_heading_caption skeleton"
          style={{width: '160px', height: '20px'}}
        />
      </div>
      <div className="section_container skeleton">
        <div className="section_container_header type_border">
          <div className="device_info_item">
            <h4 className="device_info_key body_medium_b">{t('ctn_mytrips_ev_energyuse')}</h4>
            <span className="device_info_val body_xlarge_b">
              <span className="device_info_amount body_large_b">
                {ERROR.DASH}
                <span className="body_small_r">{UNIT.KWH}</span>
              </span>
            </span>
          </div>
        </div>
        <div className="electricity_graph_area">
          <EnergyPowerRecentGraph
            motorRatio={0}
            electricDeviceRatio={0}
            climateRatio={0}
            batteryManageRatio={0}
          />
          <div className="electricty_graph_info">
            <ul className="graph_legend_list">
              <li
                className="graph_legend_item"
                role="text"
              >
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--graph-gauge-active)'}}
                />
                <span className="legend_name">{t('ctn_mytrips_ev_energyuse_driving')}</span>
                <span className="legend_value">{ERROR.DASH}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
              <li
                className="graph_legend_item"
                role="text"
              >
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--blue-custom)'}}
                />
                <span className="legend_name">{t('ctn_mytrips_ev_energyuse_electronics')}</span>
                <span className="legend_value">{ERROR.DASH}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
              <li
                className="graph_legend_item"
                role="text"
              >
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--di-mint)'}}
                />
                <span className="legend_name">{t('ctn_mytrips_ev_energyuse_climate')}</span>
                <span className="legend_value">{ERROR.DASH}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
              <li
                className="graph_legend_item"
                role="text"
              >
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--di-purple)'}}
                />
                <span className="legend_name">{t('ctn_mytrips_ev_energyuse_battery')}</span>
                <span className="legend_value">{ERROR.DASH}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <BarChartLoading />
      <div className="section_container section_container_small skeleton">
        <div className="device_info_item">
          <span className="device_info_icon">
            <span className="ic ic_battery_24px" />
          </span>
          <div className="device_info_text">
            <h4 className="device_info_key">{t('ctn_mytrips_ev_average_energyuse')}</h4>
            <span className="device_info_val">
              <span className="device_info_amount">
                <span className="device_info_number">{ERROR.DASH}</span>
                <span className="device_info_unit">{defaultEnergyEfficiencySymbol}</span>
                {defaultEnergyEfficiencySecondarySymbol !== null && (
                  <span className="device_info_convert">
                    =<span className="device_info_number">{ERROR.DASH}</span>
                    <span className="device_info_unit">{defaultEnergyEfficiencySecondarySymbol}</span>
                  </span>
                )}
              </span>
            </span>
          </div>
        </div>
      </div>
      <div className="section_container section_container_small skeleton">
        <div className="device_info_item">
          <span className="device_info_icon">
            <span className="ic ic_reservation_charge_24px" />
          </span>
          <div className="device_info_text">
            <h4 className="device_info_key">{t('ctn_mytrips_ev_regenerative_01')}</h4>
            <span className="device_info_val">
              <span className="device_info_amount">
                <span className="device_info_number">{ERROR.DASH}</span>
                <span className="device_info_unit">{UNIT.KWH}</span>
              </span>
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

function FallbackError() {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {defaultEnergyEfficiencySymbol, defaultEnergyEfficiencySecondarySymbol} = useUnitConverter();
  const today = new Date();

  return (
    <section className="section">
      <div className="section_heading type_main">
        <h3
          className="section_heading_title"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('msg_mytrips_ev_title_01')}
        </h3>
        <span
          className="section_heading_caption"
          role="text"
          aria-label={`${t('ctn_mytrips_ev_title_update_01')} ${CldrDatetimeFormatter.format(subDate(today, {days: 1}), 'yMMMd')}`}
        >
          {t('ctn_mytrips_ev_title_update_01')} {CldrDatetimeFormatter.format(subDate(today, {days: 1}), 'yMMMd')}
        </span>
      </div>
      <div className="section_container">
        <div className="section_container_header type_border">
          <div
            className="device_info_item"
            role="text"
            aria-label={`${t('ctn_mytrips_ev_energyuse')} ${ERROR.DASH}${UNIT.KWH}`}
          >
            <h4
              className="device_info_key body_medium_b"
              aria-hidden={true}
            >
              {t('ctn_mytrips_ev_energyuse')}
            </h4>
            <span className="device_info_val body_xlarge_b">
              <span className="device_info_amount body_large_b">
                {ERROR.DASH}
                <span className="body_small_r">{UNIT.KWH}</span>
              </span>
            </span>
          </div>
        </div>
        <div className="electricity_graph_area">
          <EnergyPowerRecentGraph
            motorRatio={0}
            electricDeviceRatio={0}
            climateRatio={0}
            batteryManageRatio={0}
          />
          <div className="electricty_graph_info">
            <ul className="graph_legend_list">
              <li
                className="graph_legend_item"
                role="text"
                aria-label={`${t('ctn_mytrips_ev_energyuse_driving')} ${ERROR.DASH}${UNIT.PERCENT}`}
              >
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--graph-gauge-active)'}}
                />
                <span className="legend_name">{t('ctn_mytrips_ev_energyuse_driving')}</span>
                <span className="legend_value">{ERROR.DASH}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
              <li
                className="graph_legend_item"
                role="text"
                aria-label={`${t('ctn_mytrips_ev_energyuse_electronics')} ${ERROR.DASH}${UNIT.PERCENT}`}
              >
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--blue-custom)'}}
                />
                <span className="legend_name">{t('ctn_mytrips_ev_energyuse_electronics')}</span>
                <span className="legend_value">{ERROR.DASH}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
              <li
                className="graph_legend_item"
                role="text"
                aria-label={`${t('ctn_mytrips_ev_energyuse_climate')} ${ERROR.DASH}${UNIT.PERCENT}`}
              >
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--di-mint)'}}
                />
                <span className="legend_name">{t('ctn_mytrips_ev_energyuse_climate')}</span>
                <span className="legend_value">{ERROR.DASH}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
              <li
                className="graph_legend_item"
                role="text"
                aria-label={`${t('ctn_mytrips_ev_energyuse_battery')} ${ERROR.DASH}${UNIT.PERCENT}`}
              >
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--di-purple)'}}
                />
                <span className="legend_name">{t('ctn_mytrips_ev_energyuse_battery')}</span>
                <span className="legend_value">{ERROR.DASH}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <BarChartError />
      <div
        className="section_container section_container_small"
        role="text"
        aria-label={`${t('ctn_mytrips_ev_average_energyuse')} ${ERROR.DASH}${defaultEnergyEfficiencySymbol}${defaultEnergyEfficiencySecondarySymbol !== null ? ` = ${ERROR.DASH}${defaultEnergyEfficiencySecondarySymbol}` : ''}`}
      >
        <div
          className="device_info_item"
          aria-label={`${t('ctn_mytrips_ev_average_energyuse')} ${ERROR.DASH}${defaultEnergyEfficiencySymbol}${defaultEnergyEfficiencySecondarySymbol !== null ? ` = ${ERROR.DASH}${defaultEnergyEfficiencySecondarySymbol}` : ''}`}
        >
          <span className="device_info_icon">
            <span className="ic ic_battery_24px" />
          </span>
          <div className="device_info_text">
            <h4
              className="device_info_key"
              aria-hidden={true}
            >
              {t('ctn_mytrips_ev_average_energyuse')}
            </h4>
            <span className="device_info_val">
              <span className="device_info_amount">
                <span className="device_info_number">{ERROR.DASH}</span>
                <span className="device_info_unit">{defaultEnergyEfficiencySymbol}</span>
                {defaultEnergyEfficiencySecondarySymbol !== null && (
                  <span className="device_info_convert">
                    =<span className="device_info_number">{ERROR.DASH}</span>
                    <span className="device_info_unit">{defaultEnergyEfficiencySecondarySymbol}</span>
                  </span>
                )}
              </span>
            </span>
          </div>
        </div>
      </div>
      <div
        className="section_container section_container_small"
        role="text"
        aria-label={`${t('ctn_mytrips_ev_regenerative_01')} ${ERROR.DASH}${UNIT.KWH}`}
      >
        <div
          className="device_info_item"
          aria-label={`${t('ctn_mytrips_ev_regenerative_01')} ${ERROR.DASH}${UNIT.KWH}`}
        >
          <span className="device_info_icon">
            <span className="ic ic_reservation_charge_24px" />
          </span>
          <div className="device_info_text">
            <h4
              className="device_info_key"
              aria-hidden={true}
            >
              {t('ctn_mytrips_ev_regenerative_01')}
            </h4>
            <span className="device_info_val">
              <span className="device_info_amount">
                <span className="device_info_number">{ERROR.DASH}</span>
                <span className="device_info_unit">{UNIT.KWH}</span>
              </span>
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

interface RecentEvTripHistoryBase {
  ignOnTime: string;
}

export interface RecentEvHourlyChartData<T extends RecentEvTripHistoryBase> {
  hour: number;
  value: number;
  tripHistories: Array<T>;
}

export interface RecentEvChartScale {
  maxValue: number;
  chartMaxValue: number;
  // [중간 guideline, 상단 guideline]
  guideLineValues: [number, number];
}

/**
 * ignOnTime 기준으로 trip history를 24개 시간대 버킷(0~23시)으로 고정 그룹핑합니다.
 * - 항상 24개 버킷을 반환해 비어 있는 시간대가 있어도 bar 위치가 흔들리지 않게 합니다.
 * - ignOnTime 파싱에 실패한 row는 차트에서 제외합니다.
 * - getValue가 있으면 같은 시간대의 value를 함께 누적합니다.
 */
export function getRecentEvHourlyChartData<T extends RecentEvTripHistoryBase>({
  tripHistories,
  getValue,
}: {
  tripHistories: Array<T>;
  getValue?: (tripHistory: T) => number;
}): Array<RecentEvHourlyChartData<T>> {
  const chartData = Array.from({length: 24}, (_, hour) => ({
    hour,
    value: 0,
    tripHistories: [] as Array<T>,
  }));

  tripHistories.forEach((tripHistory) => {
    const parsedIgnOnTime = parseDateWithFormat(tripHistory.ignOnTime);
    if (!parsedIgnOnTime) {
      return;
    }

    const hour = parsedIgnOnTime.getHours();
    if (!Number.isInteger(hour) || hour < 0 || hour > 23) {
      return;
    }

    const chartBucket = chartData[hour];
    chartBucket.tripHistories.push(tripHistory);

    if (!getValue) {
      return;
    }

    const nextValue = getValue(tripHistory);
    chartBucket.value += Number.isFinite(nextValue) ? nextValue : 0;
  });

  return chartData;
}

/**
 * y축 최대값은 max + margin이고, guideline은 [중간, 상단] 순서로 반환합니다.
 * 숫자가 아닌 값은 차트가 깨지지 않도록 무시하거나 0으로 보정합니다.
 */
const DEFAULT_MAX_Y_AXIS_VALUE = 10;
export function getRecentEvChartScale({values}: {values: Array<number>}): RecentEvChartScale {
  const maxValue = values.reduce((accumulator, value) => {
    if (!Number.isFinite(value)) {
      return accumulator;
    }

    return Math.max(accumulator, value, DEFAULT_MAX_Y_AXIS_VALUE);
  }, 0);

  const chartMaxValue = Math.ceil(maxValue / 10) * 10;

  return {
    maxValue,
    chartMaxValue,
    guideLineValues: [chartMaxValue / 2, chartMaxValue],
  };
}

function BarChart({vin}: {vin: string}) {
  const {data: evRecentData} = useSuspenseQuery({
    queryKey: [getQueryKey(diApiEu.drivingHistory.retrieveEvStatsOfLastDrive), vin],
    queryFn: () => diApiEu.drivingHistory.retrieveEvStatsOfLastDrive({vin}),
    select: ({data}) => data,
  });

  const validLatestDriveDate = parseDateWithFormat(evRecentData.latestDriveDate);
  if (!validLatestDriveDate) {
    throw new ExpectedError('Invalid latestDriveDate');
  }
  const params = {
    vin,
    date: getYYYYMMddFormat(validLatestDriveDate),
    displayOffsetDateTime: true,
    paging: false,
  };

  const {data: dailyTripHistory} = useSuspenseQuery({
    queryKey: [getQueryKey(diApiEu.drivingHistory.retrieveDailyTripHistory), params],
    queryFn: () => diApiEu.drivingHistory.retrieveDailyTripHistory(params),
    select: ({data}) => data,
  });

  const chartData = getRecentEvHourlyChartData({
    tripHistories: dailyTripHistory.content,
    getValue: (tripHistory) => tripHistory.evEnergyUsage.totalPowerConsumption / 1000,
  }).map(({hour, value}) => ({
    hour,
    value: roundToFirstDecimal(value),
  }));

  /**
   * guideline 규칙
   * - 상단 guideline은 step 단위로 올림한 값을 사용합니다.
   * - 상단 guideline을 2로 나눴을 때 소수점이 나오면 step을 한 번 더 올려서 중간 guideline도 정수로 맞춥니다.
   *   예) max 65, step 5 -> 65 / 2 = 32.5 이므로 70 / 35 로 보정
   * - 소수 step(0.2) 구간은 작은 값 표현을 위해 현재 값을 유지합니다.
   */

  return (
    <BarChartView
      data={chartData}
      scale={getRecentEvChartScale({
        values: chartData.map(({value}) => value),
      })}
      unit={UNIT.KWH}
    />
  );
}

interface RecentEvChartBarData {
  hour: number;
  value: number;
}

interface ChartProps {
  data: Array<RecentEvChartBarData>;
  scale: RecentEvChartScale;
  unit: string;
  fallbackText?: string;
  skeleton?: boolean;
}

interface RecentEvChartTooltipPosition {
  top: number;
  left: number;
  arrowLeft: number;
}

const RECENT_EV_CHART = {
  // 하루 24시간 bar chart를 고정 폭/고정 높이로 렌더링합니다.
  barCount: 24,
  barWidth: 6,
  barAreaHeight: 116,
  mainHeight: 144,
  barSidePadding: 8,
  tooltipGap: 9,
  // speech_bubble 화살표는 7px 정사각형을 회전해 사용하므로 edge clamp는 최소 여유만 둡니다.
  tooltipArrowPadding: 4,
} as const;

// loading / error 상태에서도 실제 차트와 같은 bar 개수와 위치를 유지합니다.
const RECENT_EV_CHART_FALLBACK_DATA: Array<RecentEvChartBarData> = Array.from({length: RECENT_EV_CHART.barCount}, (_, hour) => ({
  hour,
  value: 0,
}));

// placeholder 차트도 guideline 2개를 유지해야 y축 레이아웃이 흔들리지 않습니다.
const RECENT_EV_CHART_FALLBACK_SCALE: RecentEvChartScale = {
  maxValue: 10,
  chartMaxValue: 10,
  guideLineValues: [5, 10],
};

/**
 * bar 높이 계산용 비율입니다.
 * 잘못된 값은 0으로 처리하고, chartMaxValue를 넘으면 1로 고정합니다.
 */
function getRecentEvChartRatio(value: number, chartMaxValue: number) {
  if (!Number.isFinite(value) || value <= 0 || !Number.isFinite(chartMaxValue) || chartMaxValue <= 0) {
    return 0;
  }

  return Math.min(value / chartMaxValue, 1);
}

// value를 bar 영역 상단 기준 top(px)으로 바꿉니다. 값이 클수록 top은 위로 올라갑니다.
function getRecentEvChartTop(value: number, chartMaxValue: number) {
  return `${Math.round((1 - getRecentEvChartRatio(value, chartMaxValue)) * RECENT_EV_CHART.barAreaHeight)}px`;
}

// x축 / tooltip에 쓰는 HH:00 라벨입니다. hour + 1이 들어와도 24:00까지 그대로 표시합니다.
function getRecentEvChartHourLabel(hour: number) {
  return `${String(hour).padStart(2, '0')}:00`;
}

// 24개 bar를 좌우 padding 안에 균등 간격으로 배치하기 위한 left 계산입니다.
function getRecentEvChartLeft(hour: number) {
  return `calc(${RECENT_EV_CHART.barSidePadding}px + ((100% - ${RECENT_EV_CHART.barSidePadding * 2 + RECENT_EV_CHART.barWidth}px) / ${
    RECENT_EV_CHART.barCount - 1
  }) * ${hour})`;
}

// 초기 선택값은 가장 큰 bar 1개입니다. 전부 0이면 tooltip이 뜨지 않도록 null을 반환합니다.
function getRecentEvChartDefaultSelectedHour(data: Array<RecentEvChartBarData>) {
  let nextSelectedHour: number | null = null;
  let maxValue = 0;

  for (const {hour, value} of data) {
    if (value > maxValue) {
      maxValue = value;
      nextSelectedHour = hour;
    }
  }

  return maxValue > 0 ? nextSelectedHour : null;
}

/**
 * 실제 데이터 차트와 loading/error placeholder를 공용으로 렌더링합니다.
 * fallbackText가 있으면 클릭/포커스/tooltip을 끄고 dash 차트만 유지합니다.
 */
function BarChartView({data, scale, unit, fallbackText, skeleton = false}: ChartProps) {
  const {t} = useInferableTranslation();
  // fallback 차트는 상호작용 없이 레이아웃만 동일하게 유지합니다.
  const isFallback = fallbackText !== undefined;
  // 값이 전부 0이거나 placeholder여도 bar 높이 계산이 NaN / Infinity가 되지 않도록 방어합니다.
  const safeChartMaxValue = scale.chartMaxValue > 0 ? scale.chartMaxValue : 1;
  // 사용자가 다른 bar를 선택한 상태에서도 data가 바뀌면 기본 선택 bar를 다시 계산하기 위한 key입니다.
  const chartDataKey = data.map(({hour, value}) => `${hour}:${value}`).join('|');
  const defaultSelectedHour = getRecentEvChartDefaultSelectedHour(data);
  const barThinRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [selectedHour, setSelectedHour] = useState<number | null>(() => defaultSelectedHour);
  const [tooltipPosition, setTooltipPosition] = useState<RecentEvChartTooltipPosition | null>(null);

  useEffect(() => {
    setSelectedHour(defaultSelectedHour);
  }, [chartDataKey, defaultSelectedHour]);

  const positiveValues = data.map(({value}) => value).filter((value) => Number.isFinite(value) && value > 0);
  const maxValue = positiveValues.length ? Math.max(...positiveValues) : 0;
  const minValue = positiveValues.length ? Math.min(...positiveValues) : 0;
  const selectedData = isFallback || selectedHour === null ? undefined : data.find(({hour}) => hour === selectedHour);
  const topGuideLineTop = getRecentEvChartTop(scale.guideLineValues[1], safeChartMaxValue);
  const midGuideLineTop = getRecentEvChartTop(scale.guideLineValues[0], safeChartMaxValue);
  const topGuideLineLabel = formatNumberWithCommas(roundToFirstDecimal(scale.guideLineValues[1]));
  const midGuideLineLabel = formatNumberWithCommas(roundToFirstDecimal(scale.guideLineValues[0]));
  const maxValueLabel = fallbackText ?? formatNumberWithCommas(maxValue);
  const minValueLabel = fallbackText ?? formatNumberWithCommas(minValue);

  useLayoutEffect(() => {
    if (!selectedData || !barThinRef.current || !tooltipRef.current) {
      setTooltipPosition(null);
      return;
    }

    const activeSelectedData = selectedData;
    const barThinElement = barThinRef.current;
    const tooltipElement = tooltipRef.current;

    function updateTooltipPosition() {
      const selectedBar = barThinElement.querySelector<HTMLElement>(`[data-hour="${activeSelectedData.hour}"]`);
      const chartMain = barThinElement.querySelector<HTMLElement>('.bar_thin_main');
      if (!selectedBar || !chartMain) {
        setTooltipPosition(null);
        return;
      }

      const containerRect = barThinElement.getBoundingClientRect();
      const chartMainRect = chartMain.getBoundingClientRect();
      // 화살표는 클릭 영역이 아니라 실제로 보이는 fill 중심을 가리키는 편이 더 자연스럽습니다.
      const selectedBarTarget = selectedBar.querySelector<HTMLElement>('.bar_thin_fill') ?? selectedBar;
      const selectedBarRect = selectedBarTarget.getBoundingClientRect();
      const tooltipWidth = tooltipElement.offsetWidth;
      const tooltipHeight = tooltipElement.offsetHeight;
      const barCenter = selectedBarRect.left - containerRect.left + selectedBarRect.width / 2;
      const chartMainLeft = chartMainRect.left - containerRect.left;
      const chartMainRight = chartMainRect.right - containerRect.left;
      const minTooltipCenter = chartMainLeft + tooltipWidth / 2;
      const maxTooltipCenter = chartMainRight - tooltipWidth / 2;
      const fallbackTooltipCenter = chartMainLeft + chartMainRect.width / 2;

      // tooltip 본문은 chart 영역 안에 고정하고, 화살표만 실제 bar 중심을 가리키도록 따로 보정합니다.
      const clampedTooltipCenter = Math.min(Math.max(barCenter, minTooltipCenter), maxTooltipCenter);
      const tooltipLeft = minTooltipCenter <= maxTooltipCenter ? clampedTooltipCenter : fallbackTooltipCenter;
      const tooltipTop = selectedBarRect.top - containerRect.top - tooltipHeight - RECENT_EV_CHART.tooltipGap;
      const unclampedArrowLeft = barCenter - (tooltipLeft - tooltipWidth / 2);
      const arrowLeft = Math.min(
        Math.max(unclampedArrowLeft, RECENT_EV_CHART.tooltipArrowPadding),
        tooltipWidth - RECENT_EV_CHART.tooltipArrowPadding
      );

      setTooltipPosition({
        top: tooltipTop,
        left: tooltipLeft,
        arrowLeft,
      });
    }

    updateTooltipPosition();
    window.addEventListener('resize', updateTooltipPosition);

    return () => {
      window.removeEventListener('resize', updateTooltipPosition);
    };
  }, [chartDataKey, selectedData, unit]);

  const tooltipStyle = {
    display: 'inline-flex',
    top: `${tooltipPosition?.top ?? 0}px`,
    left: `${tooltipPosition?.left ?? 0}px`,
    visibility: tooltipPosition ? 'visible' : 'hidden',
    '--ev-arrow-left': `${tooltipPosition?.arrowLeft ?? 0}px`,
  } as CSSProperties;

  return (
    <div className={clsx('section_container', {skeleton: skeleton})}>
      <div
        ref={barThinRef}
        className="bar_thin"
        data-tooltip-mode="hour-range"
      >
        <div className="bar_thin_y_axis">
          <span
            className="bar_thin_y_label"
            style={{top: topGuideLineTop}}
          >
            {topGuideLineLabel}
          </span>
          <span
            className="bar_thin_y_label"
            style={{top: midGuideLineTop}}
          >
            {midGuideLineLabel}
          </span>
        </div>
        <div className="bar_thin_main">
          <span
            className="bar_thin_line"
            style={{top: topGuideLineTop}}
            aria-hidden="true"
          />
          <span
            className="bar_thin_line"
            style={{top: midGuideLineTop}}
            aria-hidden="true"
          />
          <div className="bar_thin_bars">
            {data.map(({hour, value}) => {
              const isSelected = hour === selectedHour;
              const hourLabel = getRecentEvChartHourLabel(hour);
              const nextHourLabel = getRecentEvChartHourLabel(hour + 1);

              return (
                <button
                  key={hour}
                  type="button"
                  className={`bar_thin_item${isSelected ? ' is-active' : ''}`}
                  data-hour={hour}
                  data-date={hourLabel}
                  data-value={value}
                  aria-label={`${hourLabel}-${nextHourLabel} ${formatNumberWithCommas(value)}${unit}`}
                  aria-pressed={isSelected}
                  disabled={isFallback}
                  tabIndex={isFallback ? -1 : 0}
                  onClick={() => {
                    if (!isFallback) {
                      setSelectedHour(hour);
                    }
                  }}
                  style={{
                    left: getRecentEvChartLeft(hour),
                    width: `${RECENT_EV_CHART.barWidth}px`,
                    padding: 0,
                    border: 'none',
                    background: 'transparent',
                  }}
                >
                  <span
                    className="bar_thin_fill"
                    style={{
                      height: `${Math.round(getRecentEvChartRatio(value, safeChartMaxValue) * RECENT_EV_CHART.barAreaHeight)}px`,
                    }}
                  />
                </button>
              );
            })}
          </div>
          <div
            className="bar_thin_x_axis"
            aria-hidden="true"
          >
            <span className="bar_thin_x_label">00:00</span>
            <span className="bar_thin_x_label">08:00</span>
            <span className="bar_thin_x_label">16:00</span>
            <span className="bar_thin_x_label">24:00</span>
          </div>
        </div>
        {selectedData && (
          <div
            ref={tooltipRef}
            className="speech_bubble bar_thin_tooltip"
            role="tooltip"
            style={tooltipStyle}
          >
            <span className="time">
              {getRecentEvChartHourLabel(selectedData.hour)}-{getRecentEvChartHourLabel(selectedData.hour + 1)}
            </span>
            <span className="wattage">
              {formatNumberWithCommas(selectedData.value)}
              {unit}
            </span>
          </div>
        )}
      </div>

      <ul className="statistics_info_list">
        <li className="statistics_info_item">
          <span className="statistics_info_key">{t('ctn_mytrips_ev_30day_max_01')}</span>
          <span className="statistics_info_val">
            <span className="statistics_info_number">{maxValueLabel}</span>
            <span className="statistics_info_unit">{unit}</span>
          </span>
        </li>
        <li className="statistics_info_item">
          <span className="statistics_info_key">{t('ctn_mytrips_ev_30day_min_01')}</span>
          <span className="statistics_info_val">
            <span className="statistics_info_number">{minValueLabel}</span>
            <span className="statistics_info_unit">{unit}</span>
          </span>
        </li>
      </ul>
    </div>
  );
}

function BarChartLoading() {
  return (
    <BarChartView
      data={RECENT_EV_CHART_FALLBACK_DATA}
      scale={RECENT_EV_CHART_FALLBACK_SCALE}
      unit={UNIT.KWH}
      fallbackText={ERROR.DASH}
      skeleton
    />
  );
}

function BarChartNone() {
  return (
    <BarChartView
      data={RECENT_EV_CHART_FALLBACK_DATA}
      scale={RECENT_EV_CHART_FALLBACK_SCALE}
      unit={UNIT.KWH}
      fallbackText="0"
    />
  );
}

function BarChartError() {
  return (
    <BarChartView
      data={RECENT_EV_CHART_FALLBACK_DATA}
      scale={RECENT_EV_CHART_FALLBACK_SCALE}
      unit={UNIT.KWH}
      fallbackText={ERROR.DASH}
    />
  );
}
